import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonIcon, Tooltip } from '@cubeyou-team/hx-react-components';

import {
  DATA_SOURCE_IDS,
  AI_GRADIENT_PROPERTIES,
} from '@discover-frontend/constants';
import translations from 'src/lang';
import { APPS_MAP } from 'src/constants/config';
import { QUERY_BUILDER_TYPES } from 'src/modules/query-builder/constants';
import { TrialRestricted, TRIAL_FEATURES } from 'src/modules/trial-paywall';
import { isAvailableAddon } from 'src/utils/AuthHelperMethods';
import { CopyAudienceButton } from './components/copy-audience-button';
import { PreviewAudienceButton } from './components/preview-audience-button';
import { DeleteAudienceButton } from './components/delete-audience-button';
import { useAiQueryBuilderAddon } from '../../../stanley-query/hooks/use-ai-query-builder-addon';
import { isStanleyQuerySupportedDataSource } from '../../../stanley-query/utils/isStanleyQuerySupportedDataSource';

const { STANLEY_QUERY: translation } = translations['en-US'];

interface QueryActionsProps {
  target: any;
  isQueryLoading: boolean;
  isQueryPreviewVisible: boolean;
  isStanleyQueryPanelVisible: boolean;
  isStanleyHistoryView: boolean;
  onStanleyQueryPanelToggle: () => void;
  onOpenStanleyHistory: () => void;
  onPreviewQueryRequest: () => void;
  onClearQueryRequest: () => void;
}

export const QueryActions: FC<QueryActionsProps> = ({
  target,
  isQueryLoading,
  isQueryPreviewVisible,
  isStanleyQueryPanelVisible,
  isStanleyHistoryView,
  onStanleyQueryPanelToggle,
  onOpenStanleyHistory,
  onPreviewQueryRequest,
  onClearQueryRequest,
}) => {
  const { hasAiQueryBuilder } = useAiQueryBuilderAddon();
  const dataSource = useSelector((state: any) => state.queryBuilder.dataSource);
  const queryBuilderType = useSelector(
    (state: any) => state.queryBuilder.queryBuilderType
  );
  const isCompareQuery = useSelector(
    (state: any) => state.queryBuilder.isCompareQuery
  );

  const isNotConversation = useMemo(
    () => queryBuilderType !== QUERY_BUILDER_TYPES.CONVERSATION,
    [queryBuilderType]
  );

  const shouldDisplayCopyAudienceButton = useMemo(
    () =>
      isNotConversation &&
      !isCompareQuery &&
      isAvailableAddon(APPS_MAP.COMPARE) &&
      dataSource === DATA_SOURCE_IDS.TWITTER,
    [isNotConversation, isCompareQuery, dataSource]
  );

  const isStanleyQuerySupported = useMemo(
    () => isNotConversation && isStanleyQuerySupportedDataSource(dataSource),
    [isNotConversation, dataSource]
  );

  const isStanleyHistoryDisabled =
    isQueryLoading ||
    !isStanleyQuerySupported ||
    (isStanleyQueryPanelVisible && isStanleyHistoryView);

  if (hasAiQueryBuilder) {
    return (
      <div className="flex items-center gap-1">
        <PreviewAudienceButton
          isQueryLoading={isQueryLoading}
          isQueryPreviewVisible={isQueryPreviewVisible}
          onPreviewQueryRequest={onPreviewQueryRequest}
          isStaleyQueryEnabled
        />
        {shouldDisplayCopyAudienceButton && (
          <CopyAudienceButton
            target={target}
            isQueryLoading={isQueryLoading}
            isStaleyQueryEnabled
          />
        )}
        {isNotConversation && (
          <DeleteAudienceButton
            target={target}
            isQueryLoading={isQueryLoading}
            onClearQueryRequest={onClearQueryRequest}
          />
        )}

        <div className="flex items-center gap-1">
          <Tooltip
            content={translation.BUTTON.STANLEY_HISTORY}
            position="bottom"
            isInfoLabel
          >
            <TrialRestricted feature={TRIAL_FEATURES.STANLEY_QUERY}>
              <ButtonIcon
                icon="query_temp"
                size="small"
                type="button"
                data-test-id="stanley-query-history-toggle"
                aria-label={translation.BUTTON.STANLEY_HISTORY}
                disabled={isStanleyHistoryDisabled}
                onClick={onOpenStanleyHistory}
              />
            </TrialRestricted>
          </Tooltip>
          <TrialRestricted feature={TRIAL_FEATURES.STANLEY_QUERY}>
            <Button
              type="button"
              data-test-id="stanley-query-toggle"
              aria-pressed={isStanleyQueryPanelVisible}
              icon="icon-ai"
              size="small"
              variant="secondary"
              gradient={AI_GRADIENT_PROPERTIES}
              disabled={
                isQueryLoading ||
                isStanleyQueryPanelVisible ||
                !isStanleyQuerySupported
              }
              onClick={onStanleyQueryPanelToggle}
            >
              {translation.BUTTON.STANLEY_QUERY}
            </Button>
          </TrialRestricted>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      {shouldDisplayCopyAudienceButton && (
        <CopyAudienceButton target={target} isQueryLoading={isQueryLoading} />
      )}
      <PreviewAudienceButton
        isQueryLoading={isQueryLoading}
        isQueryPreviewVisible={isQueryPreviewVisible}
        onPreviewQueryRequest={onPreviewQueryRequest}
      />
      {isNotConversation && (
        <DeleteAudienceButton
          target={target}
          isQueryLoading={isQueryLoading}
          onClearQueryRequest={onClearQueryRequest}
        />
      )}
    </div>
  );
};
